import React, { useMemo, useCallback, useEffect, useRef } from 'react';
import { useWatch, Controller } from 'react-hook-form';
import type { FormFieldMapping } from '../../types/fieldMapping';
import { useFormEngineForm, useFormEngineState } from '../../context/FormEngineContext';
import { AdapterMap } from '../../adapters/adapterMap';
import { resolveEditPrecisions } from '@packages/shared/forms';
import { getDataSource } from '@packages/domain-legacy';
import { ActionRegistry } from '@packages/table-builder/registry';
import { FormScrollAgreementRenderer } from '../FormScrollAgreementRenderer';
import { useReactions } from '../../processors/reactionProcessor';
import { evaluateFieldState, detectFieldStateConflicts } from '../../processors/fieldStateEvaluator';
import { prefixFieldConditions } from '../../utils/prefixFieldConditions';
import { extractFieldStateWatchPaths } from '../../utils/extractFieldWatchPaths';
import { FormField } from '@packages/ui/patterns/FormField';
import { Typography } from '@packages/ui/primitives';
import { useFormRowExcludedValues, useFormRowMeta } from '../../context/FormRowContext';
import { useChangeTrackerContext } from '../../context/ChangeTrackerContext';
import { ViewField } from '@packages/ui/patterns/ViewField';
import { MaskedValue } from '@packages/ui/patterns/ViewField/MaskedValue';
import { useAsyncOptions } from '@packages/ui/hook-form-adapters/RhfAsyncSelect';
import { RhfInfoBanner, type InfoBannerMessage } from '@packages/ui/hook-form-adapters/RhfInfoBanner';
import type { DataSource } from '@packages/ui/hook-form-adapters/RhfAsyncSelect';
import { shouldDeferAsyncOptionLabel } from '../../utils/resolveReadOnlyOptionDisplay';
import { resolveFieldDisplayValue, resolveSyncFormatter, type SyncFormatter } from '../../utils/resolveFieldDisplayValue';
import { resolveReadonlyFormatter } from '../../utils/resolveReadonlyFormatter';
import { prefixFileUploadConfig } from '../../utils/fileUploadConfig';
import { extractStaticParamsFromParamMap } from '../../utils/resolveParamMap';
import { buildAsyncSelectCascadingProps } from '../../utils/buildAsyncSelectCascadingProps';

/** Empty placeholder while async select options load — avoids flashing raw IDs. */
const ASYNC_OPTION_PENDING_DISPLAY = '-';

function resolveDisabledDisplayValue(
  form: { getValues: (name?: string) => unknown },
  displayField: string,
  namePrefix?: string,
): string {
  const path = namePrefix ? `${namePrefix}.${displayField}` : displayField;
  const val = form.getValues(path as never);
  if (val == null || val === '') return '';
  return String(val);
}

function resolveFieldAddon(field: FormFieldMapping): unknown {
  return field.adapterProps?.endAddon ?? field.adapterProps?.startAddon;
}

/**
 * Bridges FormFieldMapping → DS ViewField for the `view-fields` presentation.
 * Renders a single label-value pair using the design system component.
 * Passes through `viewDisplayOverrides` from ViewLoader → ViewField props.
 */
function ViewFieldAdapter({
  field,
  value,
  label,
  options,
  dataBag,
  needsAsyncOptionResolve = false,
  isAsyncOptionsLoading = false,
  formatter,
}: {
  field: FormFieldMapping;
  value: unknown;
  label: string;
  options?: readonly { label: string; value: unknown }[];
  dataBag?: Record<string, unknown>;
  needsAsyncOptionResolve?: boolean;
  isAsyncOptionsLoading?: boolean;
  formatter?: SyncFormatter;
}) {
  const { params } = useFormEngineState();
  const overrides = field.viewDisplayOverrides;

  const isRichFormat =
    field.def.displayFormat === 'status' ||
    field.def.displayFormat === 'badge' ||
    field.def.displayFormat === 'multiline' ||
    field.def.displayFormat === 'multiline-list';
  const deferAsyncLabel = shouldDeferAsyncOptionLabel(
    needsAsyncOptionResolve,
    isAsyncOptionsLoading,
    value,
    options,
  );
  let displayValue = isRichFormat
    ? value
    : resolveFieldDisplayValue(value, field, {
      selectOptions: options,
      formatter,
      addon: resolveFieldAddon(field),
    });
  if (
    overrides?.suffix != null &&
    displayValue != null &&
    displayValue !== '' &&
    displayValue !== '-'
  ) {
    const suffix = overrides.suffix;
    const str = String(displayValue);
    if (!str.endsWith(suffix)) {
      displayValue = `${str}${suffix}`;
    }
  }

  const maskedFieldHasData = (() => {
    if (overrides?.masked !== true) return true;

    // 1. Check explicit hasValueField from viewDisplayOverrides pipeline
    const hasValueField = overrides.maskedHasValueField;
    if (hasValueField != null) return dataBag?.[hasValueField] === true;

    // 2. Convention: check dataBag for `has{PascalField}` (e.g. dateOfBirth → hasDateOfBirth)
    if (dataBag != null) {
      const pascal = field.name.charAt(0).toUpperCase() + field.name.slice(1);
      const conventionKey = `has${pascal}`;
      if (conventionKey in dataBag) return dataBag[conventionKey] === true;
    }

    // 3. Fallback: the raw value itself determines presence
    return value != null && value !== '';
  })();

  if (overrides?.masked === true && !maskedFieldHasData) {
    displayValue = '-';
  }

  const maskedConfig = (() => {
    if (overrides?.masked !== true || !overrides.unmaskActionId) return undefined;
    if (!maskedFieldHasData) return undefined;

    const { maskedPlaceholder, unmaskActionId, maskedFormatKey } = overrides;

    const rawValueStr = value != null && value !== '' ? String(value) : null;
    const effectivePlaceholder = rawValueStr ?? maskedPlaceholder ?? '••••••••';

    return {
      placeholder: effectivePlaceholder,
      unmaskFn: async (id: string) => {
        const result = await ActionRegistry.executeData(unmaskActionId, { id });
        if (typeof result === 'string') return result;
        if (result?.value != null) return String(result.value);
        return '';
      },
      ...(maskedFormatKey != null && maskedFormatKey !== 'SSN' && maskedFormatKey !== 'TAX_ID_US' && {
        formatValue: (raw: string) =>
          resolveFieldDisplayValue(raw, field, {
            formatter,
            maskedFormatKey,
          }),
      }),
    };
  })();

  const renderValueProp = deferAsyncLabel
    ? () => ASYNC_OPTION_PENDING_DISPLAY
    : (!maskedFieldHasData && overrides?.masked === true)
      ? '-'
      : undefined;

  return (
    <ViewField
      def={field.def as unknown as import('@packages/domain').FieldDef}
      value={displayValue}
      overrideLabel={field.overrideLabel ?? label}
      icon={overrides?.icon}
      layout={overrides?.layout ?? 'detail'}
      masked={maskedConfig}
      entityId={maskedFieldHasData ? params?.id : undefined}
      truncate={overrides?.truncate}
      overridePrecision={overrides?.overridePrecision}
      overrideDisplayFormat={overrides?.renderAs === 'badge' ? 'badge' : undefined}
      extras={{ ...(field.def.adapterProps as Record<string, unknown> | undefined), ...overrides?.extras }}
      dataBag={dataBag}
      hideLabel={overrides?.hideLabel === true}
      size={overrides?.size as 'sm' | 'md' | undefined}
      renderValue={renderValueProp}
    />
  );
}

interface FormFieldAdapterProps {
  field: FormFieldMapping;
  namePrefix?: string;
  inStack?: boolean;
}

export const FormFieldAdapter = React.memo(
  function FormFieldAdapter({ field, namePrefix, inStack }: FormFieldAdapterProps): React.ReactElement | null {
    const { form, hydratingRef: formHydratingRef, hydrationEpochRef } = useFormEngineForm();
    const {
      meta,
      isReadOnly,
      viewPresentation,
      getOptionProp,
      setOptionMeta,
      modeOverrides,
      blockOverrides,
      hideRequiredMarker,
      params,
      status: formStatus,
      formSessionKey,
    } = useFormEngineState();
    const excludedByField = useFormRowExcludedValues();
    const rowMeta = useFormRowMeta();
    const changeTracker = useChangeTrackerContext();
    const hydratingRef = useRef(true);

    const fieldName =
      namePrefix && field.name === ''
        ? namePrefix
        : namePrefix
          ? `${namePrefix}.${field.name}`
          : field.name;

    const watchPaths = useMemo(
      () => extractFieldStateWatchPaths(field, namePrefix),
      [field, namePrefix],
    );

    const watchedValues = useWatch({
      control: form.control,
      name: watchPaths,
      disabled: watchPaths.length === 0,
    });

    const excludeFromFieldName = field.adapterProps?.excludeOptionValuesFrom as string | undefined;
    const excludeFromWatchPath = excludeFromFieldName
      ? namePrefix
        ? `${namePrefix}.${excludeFromFieldName}`
        : excludeFromFieldName
      : undefined;
    const peerValueForExclude = useWatch({
      control: form.control,
      name: excludeFromWatchPath as string,
      disabled: !excludeFromWatchPath,
    });
    const excludeOptionValues = useMemo((): string[] | undefined => {
      if (peerValueForExclude == null || peerValueForExclude === '') return undefined;
      return [String(peerValueForExclude)];
    }, [peerValueForExclude]);

    const prefixedField = useMemo(() => prefixFieldConditions(field, namePrefix), [field, namePrefix]);

    const reactionOverrides = useReactions({
      reactions: prefixedField.reactions,
      form: form as any,
      meta,
      getOptionProp,
      namePrefix,
    });

    // --- Unified field state evaluation (Lane 1) ---
    // NOTE: `form.getValues()` is called inside the useMemo callback rather than
    // as an inline assignment before the memo. Calling it outside produced a new
    // object reference on every render, which caused `fieldState` (and downstream
    // `resolvedDataSource`) to re-run even when watched condition values were
    // unchanged — the indirect root cause of spurious dropdown API re-calls.
    const fieldState = useMemo(() => {
      return evaluateFieldState(prefixedField, {
        formValues: form.getValues() as Record<string, unknown>,
        meta,
        getOptionProp,
        isGlobalReadOnly: isReadOnly,
        modeOverrides,
        blockOverrides,
        reactionOverrides,
        namePrefix,
      });
    }, [prefixedField, watchedValues, form, meta, getOptionProp, isReadOnly, modeOverrides, blockOverrides, reactionOverrides, namePrefix]);

    const prevVisibleRef = useRef<boolean | undefined>(undefined);

    useEffect(() => {
      const wasVisible = prevVisibleRef.current;
      if (wasVisible === undefined) {
        prevVisibleRef.current = fieldState.visible;
        return;
      }

      if (wasVisible && !fieldState.visible && fieldState.clearOnHide !== false) {
        form.setValue(fieldName as never, null as never, {
          shouldDirty: false,
          shouldValidate: false,
        });
        if (fieldState.adapterHint === 'document-list') {
          const defProps = (field.def as { adapterProps?: Record<string, unknown> })?.adapterProps ?? {};
          const blockProps = field.adapterProps ?? {};
          const docType =
            typeof blockProps.exclusiveDocumentType === 'number'
              ? blockProps.exclusiveDocumentType
              : typeof defProps.exclusiveDocumentType === 'number'
                ? defProps.exclusiveDocumentType
                : undefined;
          const clearActionId =
            typeof blockProps.exclusiveStoreClearActionId === 'string'
              ? blockProps.exclusiveStoreClearActionId
              : typeof defProps.exclusiveStoreClearActionId === 'string'
                ? defProps.exclusiveStoreClearActionId
                : undefined;
          if (docType != null && clearActionId) {
            void ActionRegistry.executeData(clearActionId, {
              documentType: String(docType),
              ...(formSessionKey ? { formSessionKey } : {}),
            });
          }
        }
      }

      prevVisibleRef.current = fieldState.visible;
    }, [
      fieldState.visible,
      fieldState.clearOnHide,
      fieldState.adapterHint,
      fieldName,
      form,
      field,
      formSessionKey,
    ]);

    useEffect(() => {
      hydratingRef.current = false;
    }, []);


    // Dev-mode conflict warnings (fires once per field mount in development)
    useMemo(() => {
      if (process.env.NODE_ENV !== 'production') {
        const warnings = detectFieldStateConflicts(prefixedField);
        for (const w of warnings) console.warn(w);
      }
    }, [prefixedField]);

    const hasAmberBorder = changeTracker?.isChanged(fieldName) ?? false;

    const adapterHint = fieldState.adapterHint;
    const AdapterComponent = AdapterMap.get(adapterHint);

    const resolvedDataSource: Record<string, unknown> | undefined = useMemo(() => {
      if (field.adapterProps?.dataSourceOverride) return field.adapterProps.dataSourceOverride as Record<string, unknown>;
      if (field.adapterProps?.dataSource) return field.adapterProps.dataSource as Record<string, unknown>;
      if (field.dataSource) return field.dataSource as Record<string, unknown>;

      if (
        field.actionId &&
        (adapterHint === 'async-select' ||
          adapterHint === 'search-picker' ||
          adapterHint === 'phone' ||
          adapterHint === 'radio')
      ) {
        const actionId = field.actionId;
        const paramMap = field.adapterProps?.paramMap as Record<string, string> | undefined;
        const staticParams = extractStaticParamsFromParamMap(paramMap);
        return {
          type: 'fn',
          cacheKey: actionId,
          ...(staticParams && { params: staticParams }),
          // Receives effectiveParams from RhfAsyncSelect (merged ds.params + resolvedParams).
          // This enables cascading selects: FETCH_STATES receives { employmentCountryId: '<uuid>' }
          // and passes it to ActionRegistry.executeData, which forwards to the action's ctx.params.
          fn: async (_search: string, params?: Record<string, string | number | boolean>) => {
            const formSnapshot = form.getValues() as Record<string, unknown>;
            const result = await ActionRegistry.executeData(
              actionId,
              (params as Record<string, string>) ?? {},
              formSnapshot,
            );
            if (Array.isArray(result)) return result;
            if (result && typeof result === 'object') {
              const obj = result as Record<string, unknown>;
              if ('items' in obj && Array.isArray(obj.items)) return obj.items;
              if ('data' in obj && Array.isArray(obj.data)) return obj.data;
            }
            return [];
          },
          mapper: (item: Record<string, unknown>) => ({
            label: String(item.label ?? item.name ?? item.participantName ?? ''),
            value: String(item.value ?? item.id ?? item.participantId ?? ''),
            ...item,
          }),
          searchMode: 'client',
        };
      }

      if (adapterHint === 'async-select' || adapterHint === 'search-picker' || adapterHint === 'phone') {
        return getDataSource(field.def as any) as Record<string, unknown> | undefined;
      }
      return undefined;
    }, [
      field.actionId,
      field.adapterProps?.dataSourceOverride,
      field.adapterProps?.dataSource,
      field.adapterProps?.paramMap,
      field.dataSource,
      field.def,
      adapterHint,
      form,
    ]);

    const defOptions = (field.def as any)?.options as readonly { label: string; value: string }[] | undefined;
    const baseOptions = fieldState.options ?? defOptions;
    const staticResolvedOptions = useMemo(() => {
      if (!baseOptions) return baseOptions;
      let options = baseOptions;
      if (excludedByField) {
        const fieldKey = field.name.split('.').pop() ?? field.name;
        const excluded = excludedByField[fieldKey];
        if (excluded?.length) {
          options = options.filter((opt) => !excluded.some((ex) => String(ex) === String(opt.value)));
        }
      }
      if (excludeOptionValues?.length) {
        options = options.filter((opt) => !excludeOptionValues.includes(String(opt.value)));
      }
      return options;
    }, [baseOptions, excludedByField, field.name, excludeOptionValues]);

    const isAsyncField = adapterHint === 'async-select' || adapterHint === 'search-picker';
    const needsAsyncOptions =
      fieldState.readOnly &&
      viewPresentation !== 'disabled-inputs' &&
      isAsyncField &&
      !staticResolvedOptions?.length;

    const needsRadioOptions =
      adapterHint === 'radio' && Boolean(field.actionId) && !staticResolvedOptions?.length;

    const { allOptions: asyncOptions, isInitialLoading: isAsyncOptionsLoading } = useAsyncOptions({
      dataSource:
        needsAsyncOptions || needsRadioOptions ? (resolvedDataSource as DataSource) : undefined,
      debounceMs: 300,
      enabled: (needsAsyncOptions || needsRadioOptions) && resolvedDataSource != null,
    });

    const resolvedOptions = useMemo(() => {
      if (staticResolvedOptions?.length) return staticResolvedOptions;
      if (asyncOptions.length > 0) {
        if (!excludeOptionValues?.length) return asyncOptions;
        return asyncOptions.filter((o) => !excludeOptionValues.includes(String(o.value)));
      }
      return staticResolvedOptions;
    }, [staticResolvedOptions, asyncOptions, excludeOptionValues]);

    const onOptionChange = useCallback((value: string, option: Record<string, unknown>) => {
      setOptionMeta(fieldName, value, option);
    }, [setOptionMeta, fieldName]);

    // View mode: async selects are not mounted, so option metadata (isOtherCountry, etc.)
    // is never seeded via onOptionChange. Seed the store when options load so
    // syncDerivedEffects can set derived flags (isOtherCountryPhone) before formatting.
    useEffect(() => {
      if (!fieldState.readOnly || viewPresentation === 'disabled-inputs') return;
      if (!isAsyncField || asyncOptions.length === 0) return;

      const currentValue = form.getValues(fieldName as never);

      const seed = (value: string, option: Record<string, unknown>) => {
        setOptionMeta(fieldName, value, option);
      };

      if (field.isMulti && Array.isArray(currentValue)) {
        for (const entry of currentValue) {
          const match = asyncOptions.find((option) => String(option.value) === String(entry));
          if (match) seed(String(entry), match as Record<string, unknown>);
        }
        return;
      }

      if (currentValue != null && currentValue !== '' && typeof currentValue !== 'object') {
        const match = asyncOptions.find((option) => String(option.value) === String(currentValue));
        if (match) seed(String(currentValue), match as Record<string, unknown>);
      }
    }, [
      fieldState.readOnly,
      viewPresentation,
      isAsyncField,
      asyncOptions,
      fieldName,
      field.isMulti,
      form,
      setOptionMeta,
    ]);

    const editFormatter = useMemo((): SyncFormatter | undefined => {
      if (fieldState.formatter) return fieldState.formatter;
      return resolveSyncFormatter(field.def.formatter as FormFieldMapping['def']['formatter']);
    }, [field.def.formatter, fieldState.formatter]);

    const readonlyDisplayFormatter = useMemo(() => {
      return resolveReadonlyFormatter(field, fieldState, getOptionProp);
    }, [field, fieldState, getOptionProp, watchedValues]);

    const editPrecision = useMemo(() => {
      const def = field.def as FormFieldMapping['def'] & {
        type?: string;
        inputPrecision?: number;
        displayPrecision?: number;
        displayFormat?: string;
      };
      const hasExplicitPrecision =
        def.inputPrecision != null
        || def.displayPrecision != null
        || def.displayFormat === 'currency'
        || def.displayFormat === 'percentage';

      const isNumericAdapter = adapterHint === 'number';

      if (!hasExplicitPrecision && !isNumericAdapter) {
        return undefined;
      }
      return resolveEditPrecisions(def as Parameters<typeof resolveEditPrecisions>[0]);
    }, [field.def, adapterHint]);

    if (!fieldState.visible) {
      return null;
    }

    if (adapterHint === 'info-banner') {
      const bannerProps = {
        ...(field.def.adapterProps as Record<string, unknown> | undefined),
        ...field.adapterProps,
      };
      const watchFieldRaw = String(bannerProps.watchField ?? '');
      const watchField =
        namePrefix && watchFieldRaw ? `${namePrefix}.${watchFieldRaw}` : watchFieldRaw;
      if (!watchField) return null;
      return (
        <RhfInfoBanner
          control={form.control}
          watchField={watchField}
          variant={bannerProps.variant as 'info' | 'success' | 'warning' | 'error' | undefined}
          semanticId={
            typeof bannerProps.semanticId === 'string' ? bannerProps.semanticId : fieldName
          }
          className={typeof bannerProps.className === 'string' ? bannerProps.className : undefined}
          messageWhenTrue={bannerProps.messageWhenTrue as InfoBannerMessage | undefined}
          messageWhenFalse={bannerProps.messageWhenFalse as InfoBannerMessage | undefined}
        />
      );
    }

    if (!AdapterComponent) {
      return null;
    }

    if (field.adapterProps?.rendererKey === 'scroll-agreement') {
      return (
        <FormScrollAgreementRenderer
          content={String(field.adapterProps.content ?? '')}
          name={fieldName}
        />
      );
    }

    if (fieldState.readOnly && viewPresentation === 'view-fields' && !fieldState.rendersOwnReadOnly) {
      const dataBag = form.getValues() as Record<string, unknown>;
      return (
        <Controller
          name={fieldName}
          control={form.control}
          render={({ field: rhfField }) => (
            <ViewFieldAdapter
              field={field}
              value={rhfField.value}
              label={fieldState.label}
              options={resolvedOptions}
              dataBag={dataBag}
              needsAsyncOptionResolve={needsAsyncOptions}
              isAsyncOptionsLoading={isAsyncOptionsLoading}
              formatter={readonlyDisplayFormatter}
            />
          )}
        />
      );
    }

    if (fieldState.readOnly && viewPresentation !== 'disabled-inputs' && !fieldState.rendersOwnReadOnly) {
      const maskedOverrides = field.viewDisplayOverrides;
      const isMasked = maskedOverrides?.masked === true && maskedOverrides.unmaskActionId != null;

      return (
        <Controller
          name={fieldName}
          control={form.control}
          render={({ field: rhfField }) => {
            if (isMasked && params?.id != null) {
              const formValues = form.getValues() as Record<string, unknown>;

              const hasValueField = maskedOverrides?.maskedHasValueField;
              let fieldHasData: boolean;
              if (hasValueField != null) {
                fieldHasData = formValues[hasValueField] === true;
              } else {
                const baseName = fieldName.includes('.') ? fieldName.split('.').pop()! : fieldName;
                const pascal = baseName.charAt(0).toUpperCase() + baseName.slice(1);
                const conventionKey = `has${pascal}`;
                if (conventionKey in formValues) {
                  fieldHasData = formValues[conventionKey] === true;
                } else {
                  fieldHasData = rhfField.value != null && rhfField.value !== '';
                }
              }

              if (!fieldHasData) {
                return (
                  <FormField label={fieldState.label} semanticId={fieldName} required={false}>
                    <Typography variant='body'>-</Typography>
                  </FormField>
                );
              }

              const { maskedPlaceholder, unmaskActionId, maskedFormatKey } = maskedOverrides!;
              const rawValueStr = rhfField.value != null && rhfField.value !== '' ? String(rhfField.value) : null;
              const maskedConfig = {
                placeholder: rawValueStr ?? maskedPlaceholder ?? '••••••••',
                unmaskFn: async (id: string) => {
                  const result = await ActionRegistry.executeData(unmaskActionId!, { id });
                  if (typeof result === 'string') return result;
                  if (result?.value != null) return String(result.value);
                  return '';
                },
                ...(maskedFormatKey != null && maskedFormatKey !== 'SSN' && maskedFormatKey !== 'TAX_ID_US' && {
                  formatValue: (raw: string) =>
                    resolveFieldDisplayValue(raw, field, {
                      formatter: readonlyDisplayFormatter,
                      maskedFormatKey,
                    }),
                }),
              };
              return (
                <FormField label={fieldState.label} semanticId={fieldName} required={false}>
                  <Typography variant='body'>
                    <MaskedValue config={maskedConfig} entityId={params.id} />
                  </Typography>
                </FormField>
              );
            }

            const deferAsyncLabel = shouldDeferAsyncOptionLabel(
              needsAsyncOptions,
              isAsyncOptionsLoading,
              rhfField.value,
              resolvedOptions,
            );

            return (
              <FormField
                label={fieldState.label}
                semanticId={fieldName}
                required={false}
              >
                <Typography variant='body'>
                  {deferAsyncLabel
                    ? ASYNC_OPTION_PENDING_DISPLAY
                    : resolveFieldDisplayValue(rhfField.value, field, {
                      selectOptions: resolvedOptions,
                      formatter: readonlyDisplayFormatter,
                      addon: resolveFieldAddon(field),
                    })}
                </Typography>
              </FormField>
            );
          }}
        />
      );
    }

    const isAsyncSelect = adapterHint === 'async-select' || adapterHint === 'search-picker';
    const isDocumentList = adapterHint === 'document-list';
    const isPhone = adapterHint === 'phone';
    const isPassword = adapterHint === 'password';
    const isFileUpload = adapterHint === 'file-upload';

    const prefixedFileUpload = useMemo(
      () => (field.fileUpload ? prefixFileUploadConfig(field.fileUpload, namePrefix) : undefined),
      [field.fileUpload, namePrefix],
    );

    const isCheckboxOrToggle = adapterHint === 'checkbox' || adapterHint === 'toggle';

    const resolvedAdapterProps = useMemo(() => {
      // Merge: def-level adapterProps (base) ← block-level adapterProps (override)
      const defProps = (field.def as any)?.adapterProps ?? {};
      const props = { ...defProps, ...(field.adapterProps ?? {}) };
      if (isPassword) props.type = 'password';
      if (isPhone && namePrefix) {
        if (typeof props.codeName === 'string') {
          props.codeName = `${namePrefix}.${props.codeName}`;
        }
        if (typeof props.watchField === 'string') {
          props.watchField = `${namePrefix}.${props.watchField}`;
        }
        if (typeof props.sourceField === 'string') {
          props.sourceField = `${namePrefix}.${props.sourceField}`;
        }
      }
      // Prefix dataSourceParams field paths for repeatable mode
      if (namePrefix && props.dataSourceParams && typeof props.dataSourceParams === 'object') {
        const prefixed: Record<string, string> = {};
        for (const [apiParam, fieldPath] of Object.entries(props.dataSourceParams as Record<string, string>)) {
          prefixed[apiParam] = `${namePrefix}.${fieldPath}`;
        }
        props.dataSourceParams = prefixed;
      }
      if (reactionOverrides.minDate !== undefined) props.minDate = reactionOverrides.minDate;
      if (reactionOverrides.maxDate !== undefined) props.maxDate = reactionOverrides.maxDate;
      if (isCheckboxOrToggle && !inStack && props.reserveLabelSpace == null) {
        props.reserveLabelSpace = true;
      }
      return props;
    }, [field.adapterProps, isPhone, isPassword, isCheckboxOrToggle, inStack, namePrefix, reactionOverrides.minDate, reactionOverrides.maxDate]);

    // Phone stores option meta under the *code* field path (not the phone number
    // field). Block effects use optionProp on workNumberCountryCodeId.
    const onPhoneCodeOptionChange = useCallback(
      (value: string, option: Record<string, unknown>) => {
        const codePath = resolvedAdapterProps.codeName as string | undefined;
        if (!codePath) return;
        setOptionMeta(codePath, value, option);
      },
      [resolvedAdapterProps.codeName, setOptionMeta],
    );

    const exclusiveDocumentType =
      typeof resolvedAdapterProps.exclusiveDocumentType === 'number'
        ? resolvedAdapterProps.exclusiveDocumentType
        : undefined;
    const exclusiveStoreClearActionId =
      typeof resolvedAdapterProps.exclusiveStoreClearActionId === 'string'
        ? resolvedAdapterProps.exclusiveStoreClearActionId
        : undefined;

    const onExclusiveStoreClear = useCallback(() => {
      if (exclusiveDocumentType == null || !exclusiveStoreClearActionId) return;
      void ActionRegistry.executeData(exclusiveStoreClearActionId, {
        documentType: String(exclusiveDocumentType),
        ...(formSessionKey ? { formSessionKey } : {}),
      });
    }, [exclusiveDocumentType, exclusiveStoreClearActionId, formSessionKey]);

    const exclusiveDropdownPath = useMemo(() => {
      const dropdown = resolvedAdapterProps.exclusiveDropdown;
      if (typeof dropdown !== 'string' || dropdown.length === 0) return undefined;
      return namePrefix ? `${namePrefix}.${dropdown}` : dropdown;
    }, [resolvedAdapterProps.exclusiveDropdown, namePrefix]);

    const cascadingProps = useMemo(() => {
      // --- URL-mode: dependsOn / paramMap wired via dataSourceParams ---
      if (resolvedDataSource?.type === 'url') {
        const urlDs = resolvedDataSource as { dependsOn?: string; paramMap?: Record<string, string> };
        return buildAsyncSelectCascadingProps(urlDs.paramMap, urlDs.dependsOn, namePrefix);
      }

      // --- fn-mode (actionId): dependsOn stored in adapterProps ---
      if (resolvedDataSource?.type === 'fn') {
        const dependsOn = field.adapterProps?.dependsOn as string | undefined;
        const paramMap = field.adapterProps?.paramMap as Record<string, string> | undefined;
        return buildAsyncSelectCascadingProps(paramMap, dependsOn, namePrefix);
      }

      return {};
    }, [resolvedDataSource, field.adapterProps, namePrefix, resolvedAdapterProps.resetOnParamsChange]);

    const fieldValue = useWatch({ control: form.control, name: fieldName });
    const prevFieldValueRef = useRef<unknown>(fieldValue);
    useEffect(() => {
      if (hydratingRef.current) return;
      if (prevFieldValueRef.current !== fieldValue && changeTracker) {
        changeTracker.markChanged(fieldName);
      }
      prevFieldValueRef.current = fieldValue;
    }, [fieldValue, changeTracker, fieldName]);

    const amberBorderStyle = hasAmberBorder
      ? { borderLeft: '3px solid #f59e0b', paddingLeft: 8, borderRadius: 4, transition: 'border-color 0.2s' }
      : undefined;

    const suppressLabel = rowMeta?.tablePresentation === true;

    // disabled-inputs keeps the required asterisk (form layout is intact).
    // label-value is handled above with required={false}.
    // hideRequiredMarker is a schema-level opt-out for any presentation.
    const displayRequired = hideRequiredMarker ? false : fieldState.required;

    const fieldTooltip =
      typeof resolvedAdapterProps.tooltip === 'string' ? resolvedAdapterProps.tooltip : undefined;
    const fieldHint =
      typeof resolvedAdapterProps.hint === 'string' ? resolvedAdapterProps.hint : undefined;
    const reselectSameValueActionId =
      typeof resolvedAdapterProps.reselectSameValueActionId === 'string'
        ? resolvedAdapterProps.reselectSameValueActionId
        : typeof resolvedAdapterProps.actionId === 'string'
          ? resolvedAdapterProps.actionId
          : undefined;
    const reselectOptionLabel =
      typeof resolvedAdapterProps.reselectOptionLabel === 'string'
        ? resolvedAdapterProps.reselectOptionLabel
        : undefined;

    const onReselectSameOption = useCallback(() => {
      if (!reselectSameValueActionId) return;
      const values = form.getValues() as Record<string, unknown>;
      const mode = (meta?.mode ?? formStatus) as 'create' | 'edit' | 'view' | undefined;
      void ActionRegistry.executeData(reselectSameValueActionId, {}, values, { mode });
    }, [reselectSameValueActionId, form, meta, formStatus]);

    const contextIdParamKey =
      typeof resolvedAdapterProps.contextIdParamKey === 'string'
        ? resolvedAdapterProps.contextIdParamKey.trim()
        : '';
    const contextIdFromParams =
      contextIdParamKey && params?.[contextIdParamKey] != null
        ? String(params[contextIdParamKey])
        : undefined;
    const {
      tooltip: _tooltipProp,
      hint: _hintProp,
      exclusiveDocumentType: _exclusiveDocumentType,
      exclusiveStoreClearActionId: _exclusiveStoreClearActionId,
      exclusiveDropdown: _exclusiveDropdown,
      excludeOptionValuesFrom: _excludeOptionValuesFrom,
      resetOnParamsChange: _resetOnParamsChange,
      reselectSameValueActionId: _reselectSameValueActionId,
      reselectOptionLabel: _reselectOptionLabelProp,
      actionId: _reselectAdapterActionId,
      contextIdParamKey: _contextIdParamKey,
      ...adapterPropsForSpread
    } = resolvedAdapterProps;

    const adapter = (
      <AdapterComponent
        name={fieldName}
        control={form.control}
        label={suppressLabel ? '' : fieldState.label}
        {...(fieldState.placeholder ? { placeholder: fieldState.placeholder } : {})}
        {...(fieldState.disabled && field.disabledDisplayField ? { placeholder: resolveDisabledDisplayValue(form, field.disabledDisplayField, namePrefix) } : {})}
        required={suppressLabel ? false : displayRequired}
        disabled={fieldState.disabled}
        semanticId={fieldName}
        {...(fieldHint != null && { hint: fieldHint })}
        {...(fieldTooltip != null && { tooltip: fieldTooltip })}
        {...(resolvedOptions !== undefined && { options: resolvedOptions })}
        {...(resolvedDataSource !== undefined && { dataSource: resolvedDataSource })}
        {...(field.isMulti !== undefined && { isMulti: field.isMulti })}
        {...(field.isClearable !== undefined && { isClearable: field.isClearable })}
        {...(isAsyncSelect && { onOptionChange, setValue: form.setValue })}
        {...(isAsyncSelect &&
          exclusiveDocumentType != null &&
          exclusiveStoreClearActionId != null && {
            onExclusiveStoreClear,
          })}
        {...(isDocumentList && {
          setValue: form.setValue,
          getValues: form.getValues,
          hydratingRef: formHydratingRef,
          hydrationEpoch: hydrationEpochRef?.current ?? 0,
          formStatus,
          formSessionKey,
          formMode: meta.mode,
          ...(contextIdFromParams !== undefined && { contextIdFromParams }),
          ...(exclusiveDropdownPath != null && {
            exclusiveDropdown: exclusiveDropdownPath,
          }),
        })}
        {...(isAsyncSelect && cascadingProps)}
        {...(isAsyncSelect && excludeOptionValues != null && { excludeOptionValues })}
        {...(isAsyncSelect &&
          reselectSameValueActionId != null &&
          reselectOptionLabel != null && {
            reselectOptionLabel,
            onReselectSameOption,
          })}
        {...(isPhone && {
          setValue: form.setValue,
          onOptionChange: onPhoneCodeOptionChange,
        })}
        {...(editFormatter !== undefined && { formatter: editFormatter })}
        {...(editPrecision != null && { editPrecision })}
        {...(adapterHint === 'label' && {
          displayFormat: field.def.displayFormat,
          badgeVariant: field.def.badgeVariant,
          badgeTemplate: field.def.badgeTemplate,
        })}
        {...adapterPropsForSpread}
        {...(inStack ? { inStack: true } : {})}
        {...(isFileUpload &&
          prefixedFileUpload && {
          fileUpload: prefixedFileUpload,
          setValue: form.setValue,
          accept: resolvedAdapterProps.accept as string | undefined,
          maxSize: resolvedAdapterProps.maxSize as number | undefined,
        })}
      />
    );

    if (amberBorderStyle) {
      return <div style={amberBorderStyle}>{adapter}</div>;
    }

    return adapter;
  },
);
